import {
  OntologyContextType,
  OntologyRolodexEntryType,
  useOntology,
} from 'features/dynamicOntology'
import { RolodexEntryQueryBody } from 'features/rolodex'
import { useMemo } from 'react'
import { GeckoEntry, RolodexEntryType } from 'types'

// FLUP this should probably be moved to the ontology as an ontology util - ie the ability to find links in the reverse direction.
const getChildTypesOfType = (
  type: OntologyRolodexEntryType,
  ontology: OntologyContextType
): ReadonlyArray<RolodexEntryType> => {
  return Object.entries(ontology.ontology.types).reduce(
    (acc, [possibleChildType, typeDef]) => {
      if (typeDef.requiredTags.includes(type)) {
        acc.push(possibleChildType as RolodexEntryType)
      }
      return acc
    },
    [] as Array<RolodexEntryType>
  )
}

const buildChildQuery = (
  entry: GeckoEntry,
  childType: OntologyRolodexEntryType
): RolodexEntryQueryBody => {
  return {
    type: childType as RolodexEntryType,
    tags: { [entry.type]: entry.id },
  }
}

export function useLinkedQueries(entry: GeckoEntry): {
  parentQueries: Record<string, RolodexEntryQueryBody>
  childQueries: Record<string, RolodexEntryQueryBody>
} {
  const ontology = useOntology()
  const childTypes = useMemo<ReadonlyArray<RolodexEntryType>>(
    () => getChildTypesOfType(entry.type as RolodexEntryType, ontology),
    [entry, ontology]
  )
  const childQueries = useMemo(
    () =>
      Object.fromEntries(
        childTypes
          .map(
            (childType) =>
              [childType, addOrderById(buildChildQuery(entry, childType))] as [
                RolodexEntryType,
                RolodexEntryQueryBody,
              ]
          )
          .sort((a, b) => {
            return (
              getHierarchyLevel(a[0], ontology.ontology.types) -
              getHierarchyLevel(b[0], ontology.ontology.types)
            )
          })
      ),
    [childTypes, entry, ontology.ontology.types]
  )

  const parentTypes = useMemo(
    () =>
      ontology.ontology.types[entry.type]?.requiredTags.map(
        (type) => type as RolodexEntryType
      ) ?? [],
    [entry, ontology]
  )

  const parentQueries = useMemo(
    () =>
      Object.fromEntries(
        parentTypes
          .map(
            (type) =>
              [type, { type, ids: [entry.tags[type]] }] as [
                RolodexEntryType,
                RolodexEntryQueryBody,
              ]
          )
          .filter(([, { ids }]) => ids?.length)
          .sort((a, b) => {
            return (
              getHierarchyLevel(a[0], ontology.ontology.types) -
              getHierarchyLevel(b[0], ontology.ontology.types)
            )
          })
          .map(([type, query]) => [type, addOrderById(query)])
      ),
    [entry.tags, ontology.ontology.types, parentTypes]
  )

  return {
    parentQueries,
    childQueries,
  }
}

function addOrderById(query: RolodexEntryQueryBody): RolodexEntryQueryBody {
  return {
    ...query,
    order_by: [{ field: 'id', descenting: true }],
  }
}

const getHierarchyLevel = (
  type: RolodexEntryType,
  types: Readonly<Record<string, { requiredTags: ReadonlyArray<string> }>>
): number => {
  return types[type]?.requiredTags?.length ?? 999 // Unknown types go to bottom
}
